import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { TableCard } from "@/components/shared/TableCard";
import { TableActionsMenu } from "@/components/shared/TableActionsMenu";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  listEnterpriseProducts,
  deleteEnterpriseProduct,
  type EnterpriseProduct,
} from "@/services/platformEnterprisesService";
import { formatCatalogAmountMinor } from "@/services/certificatePricingCatalogService";

export function EnterpriseProductsTab({
  enterpriseId,
}: {
  enterpriseId: string;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const queryKey = ["platform", "enterprises", enterpriseId, "products"] as const;

  const productsQuery = useQuery({
    queryKey,
    queryFn: () => listEnterpriseProducts(enterpriseId),
  });

  const [deleteTarget, setDeleteTarget] = useState<EnterpriseProduct | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (productId: string) =>
      deleteEnterpriseProduct(enterpriseId, productId),
    onSuccess: () => {
      toast.success(t("platformEnterprises.deleteProductSuccess"));
      void queryClient.invalidateQueries({ queryKey });
      setDeleteTarget(null);
    },
    onError: () => {
      toast.error(t("platformEnterprises.deleteProductError"));
    },
  });

  function openCreate(): void {
    navigate(`/platform/enterprises/${enterpriseId}/products/new`);
  }

  function openEdit(product: EnterpriseProduct): void {
    navigate(`/platform/enterprises/${enterpriseId}/products/${product.id}/edit`);
  }

  if (productsQuery.isPending) {
    return (
      <TableCard>
        <div className="flex min-h-[30vh] items-center justify-center py-12">
          <LoadingLogo size="lg" />
        </div>
      </TableCard>
    );
  }

  const products = productsQuery.data ?? [];

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Button size="sm" onClick={openCreate}>
          <Plus className="mr-1.5 h-4 w-4" />
          {t("platformEnterprises.addProduct")}
        </Button>
      </div>

      {products.length === 0 ? (
        <TableCard>
          <div className="flex min-h-[30vh] items-center justify-center py-12">
            <EmptyState
              title={t("platformEnterprises.productsEmptyTitle")}
              message={t("platformEnterprises.productsEmptyMessage")}
            />
          </div>
        </TableCard>
      ) : (
        <TableCard>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("platformEnterprises.productsColName")}</TableHead>
                  <TableHead>{t("platformEnterprises.productsColSlug")}</TableHead>
                  <TableHead>{t("platformEnterprises.productsColCategory")}</TableHead>
                  <TableHead className="text-right">
                    {t("platformEnterprises.productsColPrice")}
                  </TableHead>
                  <TableHead>{t("platformEnterprises.productsColActive")}</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() => openEdit(p)}
                  >
                    <TableCell className="font-medium text-foreground">
                      {p.displayName}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {p.slug}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{p.category}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCatalogAmountMinor(p.amountMinor, p.currency, i18n.language)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={p.isActive ? "default" : "secondary"}>
                        {p.isActive
                          ? t("platformEnterprises.productsActiveYes")
                          : t("platformEnterprises.productsActiveNo")}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <TableActionsMenu
                        actions={[
                          {
                            label: t("platformEnterprises.editProduct"),
                            icon: Pencil,
                            onClick: () => openEdit(p),
                          },
                          {
                            label: t("platformEnterprises.deleteProduct"),
                            icon: Trash2,
                            onClick: () => setDeleteTarget(p),
                            variant: "destructive",
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TableCard>
      )}

      <ConfirmationDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t("platformEnterprises.deleteProductTitle")}
        description={t("platformEnterprises.deleteProductDescription", {
          name: deleteTarget?.displayName ?? "",
        })}
        confirmText={t("platformEnterprises.deleteProduct")}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
        }}
        isLoading={deleteMutation.isPending}
        variant="destructive"
      />
    </>
  );
}
